import React, { useMemo } from "react";

import { makeStyles } from "@mui/styles";

import { kickLightBlue, kickNavy } from "../styles/portalStyles";

import { displayFriendlyLongDate } from "~/shared/utils/sharedUtils";

import { addressLines } from "~/patient-portal/utils/treatmentCard";

import { medicationLabel } from "~/patient-portal/utils/medicationLabel";

import { buildShipmentRows } from "~/patient-portal/utils/shipmentHistory";

const useStyles = makeStyles(() => ({
  section: {
    marginTop: 40,
  },
  sectionTitle: {
    fontFamily: "BasisGrotesquePro-Bold",
    fontSize: 24,
    color: kickNavy,
    margin: "0 0 12px",
  },
  table: {
    border: "1px solid #e4e4ee",
    borderRadius: 10,
    overflow: "hidden",
    background: "#fff",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1.9fr 1fr 1.5fr",
    gap: 8,
    padding: "11px 14px",
    fontSize: 14,
    color: "#444",
    alignItems: "start",
  },
  headerRow: {
    fontFamily: "BasisGrotesquePro-Bold",
    color: kickNavy,
    background: kickLightBlue,
    fontSize: 12.5,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  zebra: {
    borderTop: "1px solid #eee",
  },
  medLine: {
    fontFamily: "BasisGrotesquePro-Bold",
    color: kickNavy,
  },
  badges: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 6,
  },
  badge: {
    display: "inline-block",
    padding: "1px 8px",
    borderRadius: 10,
    background: kickLightBlue,
    color: kickNavy,
    fontSize: 11,
    fontFamily: "BasisGrotesquePro-Bold",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  muted: {
    color: "#999",
  },
  addrLine: {
    fontSize: 13,
    color: "#666",
  },
  // mobile stacked card
  stackedItem: {
    border: "1px solid #e4e4ee",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 10,
    fontSize: 14,
    color: "#444",
    background: "#fff",
  },
  stackedLabel: {
    color: "#888",
    fontSize: 12.5,
    marginTop: 6,
  },
}));

// The clinic badge and any Refill badge sit above the medication(s);
// a single shipment can cover a combo of up to four prescriptions, one per line.
function MedsCell({ classes, meds, orderItems, clinic, clinicColor, badge }) {
  return (
    <div>
      {(clinic || badge) && (
        <div className={classes.badges}>
          {clinic && (
            <span className={classes.badge} style={{ background: clinicColor }}>
              {clinic}
            </span>
          )}
          {badge && <span className={classes.badge}>{badge}</span>}
        </div>
      )}
      {meds.map((rx, i) => (
        <div className={classes.medLine} key={rx.id || i}>
          {medicationLabel(rx, orderItems)}
        </div>
      ))}
    </div>
  );
}

function DeliveredTo({ classes, address }) {
  const lines = addressLines({
    address1: address.address1,
    address2: address.address2,
    city: address.city,
    state: address.usState,
    zip: address.zip,
  });
  if (lines.length === 0) return <span className={classes.muted}>—</span>;
  return (
    <div>
      {lines.map((line, i) => (
        <div className={classes.addrLine} key={i}>
          {line}
        </div>
      ))}
    </div>
  );
}

// Shipment History: a single flat, newest-first log of every mail shipment the
// patient has received — original orders, refills, and autoship alike. Renders
// nothing until at least one shipment exists (the prescription section above
// already covers the brand-new-account empty state).
export default function PortalShipmentHistory({ visits, isMobile }) {
  const classes = useStyles();
  const rows = useMemo(() => buildShipmentRows(visits), [visits]);

  if (rows.length === 0) return null;

  if (isMobile) {
    return (
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Shipment History</div>
        {rows.map((row) => (
          <div className={classes.stackedItem} key={row.key}>
            <MedsCell
              classes={classes}
              meds={row.meds}
              orderItems={row.visit.orderItems}
              clinic={row.clinic}
              clinicColor={row.clinicColor}
              badge={row.badge}
            />
            <div className={classes.stackedLabel}>
              Sent {displayFriendlyLongDate(row.dateSent)}
            </div>
            <div style={{ marginTop: 6 }}>
              <DeliveredTo classes={classes} address={row.address} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={classes.section}>
      <div className={classes.sectionTitle}>Shipment History</div>
      <div className={classes.table}>
        <div className={`${classes.row} ${classes.headerRow}`}>
          <div>Medication</div>
          <div>Date Sent</div>
          <div>Delivered To</div>
        </div>
        {rows.map((row, idx) => (
          <div
            className={`${classes.row} ${idx > 0 ? classes.zebra : ""}`}
            key={row.key}
          >
            <MedsCell
              classes={classes}
              meds={row.meds}
              orderItems={row.visit.orderItems}
              clinic={row.clinic}
              clinicColor={row.clinicColor}
              badge={row.badge}
            />
            <div>{displayFriendlyLongDate(row.dateSent)}</div>
            <DeliveredTo classes={classes} address={row.address} />
          </div>
        ))}
      </div>
    </div>
  );
}
